import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { ApiService } from './api.service';
import {
  mapEarlyAccessGateError,
  Prepurchase,
  PrepurchaseEnvelope,
  PrepurchaseError,
  PrepurchaseRequest,
  PrepurchaseSession,
  PrepurchaseSessionRequest,
  unwrapPrepurchase,
} from '../models/prepurchase.model';
import { Paginated, Product } from '../models/product.model';
import { toUiProduct } from '../utils/product.utils';
import { PREPURCHASE_COPY } from '../../features/checkout/checkout-copy';

/**
 * Aplana el error que devuelve Laravel cuando falla un validador.
 * Acepta cualquiera de las formas que el backend usa hoy:
 *   - string plano  → se devuelve tal cual
 *   - { campo: ["msg1", "msg2"] }  → join de los mensajes
 *   - { campo: "msg" }  → join de los valores
 * Devuelve '' cuando el payload no aporta texto utilizable.
 */
function flattenLaravelError(payload: unknown): string {
  if (!payload) return '';
  if (typeof payload === 'string') return payload;
  if (typeof payload === 'object') {
    const values = Object.values(payload as Record<string, unknown>).flat();
    const flat = values.filter((v): v is string => typeof v === 'string');
    if (flat.length > 0) return flat.join(' ');
  }
  return '';
}

/**
 * Canal independiente del `CartService`. Las precompras viven fuera del
 * carrito: el usuario llena un formulario por cada producto y el backend
 * crea un `Prepurchase` individual con estado `nuevo` y el 30% de abono ya
 * declarado (comprobante obligatorio en el mismo multipart, Bloque 1).
 *
 * Endpoints que consumimos:
 *  - GET  /prepurchase-products        → listado público (no paginado).
 *  - POST /prepurchase-sessions        → abre la reserva flotante de 15 min
 *    (Bloque 2, D5): se llama al pasar al paso de pago; 409 = cupo derivado
 *    agotado (razón `sold_out`, rótulo verbatim).
 *  - GET  /prepurchase-sessions/{token} → estado de la reserva (rehidratación).
 *  - POST /new-web-prepurchase         → crea precompra (multipart con
 *    `payment_method` + `session_token` + comprobante `image`), auth
 *    client.credentials. Consume la sesión.
 *
 * El backend expone los productos con sus flags `prepurchase_*` ya listos,
 * así que aplicamos `toUiProduct` igual que el catálogo regular para
 * reutilizar ProductCard sin adaptadores.
 */
@Injectable({ providedIn: 'root' })
export class PrepurchaseService {
  private readonly api = inject(ApiService);

  /**
   * Listado de productos disponibles para precompra. El backend filtra por
   * `prepurchase=true AND enabled=true AND prepurchase_stock > 0` — el
   * frontend no necesita re-validar. Puede devolver un array directo o
   * envuelto en `{ data: [...] }` según el helper `showAll`.
   */
  getPrepurchaseProducts(): Observable<Product[]> {
    return this.api
      .get<Product[] | { data: Product[] } | Paginated<Product>>('/prepurchase-products', {
        skip_paging: true,
      })
      .pipe(
        map((res) => {
          if (Array.isArray(res)) return res;
          if (res && typeof res === 'object' && 'data' in res) {
            const data = (res as { data: Product[] }).data;
            return Array.isArray(data) ? data : [];
          }
          return [];
        }),
        map((products) => products.map(toUiProduct)),
      );
  }

  /**
   * Abre la reserva flotante de 15 minutos (Bloque 2): reserva `quantity`
   * unidades del cupo derivado (`prepurchase_stock − SUM(sesiones vigentes)`)
   * y devuelve el token + `expires_at` absoluto para el countdown +
   * `amount_colones` (30 % server-side, importe autoritativo).
   *
   * Errores → `PrepurchaseError`:
   *  - 409 → `sold_out` con el rótulo VERBATIM del copy centralizado.
   *  - 422 → límites por cliente / validación (mismo mapeo que la creación).
   */
  openSession(payload: PrepurchaseSessionRequest): Observable<PrepurchaseSession> {
    return this.api
      .post<PrepurchaseSession>('/prepurchase-sessions', payload)
      .pipe(catchError((err) => this.handlePrepurchaseError(err)));
  }

  /**
   * Estado actual de una reserva (rehidratación del countdown tras un
   * refresh). Una `pending` vencida ya llega como `expired`: el backend
   * expira implícitamente.
   */
  getSession(token: string): Observable<PrepurchaseSession> {
    return this.api
      .get<PrepurchaseSession>(`/prepurchase-sessions/${token}`)
      .pipe(catchError((err) => this.handlePrepurchaseError(err)));
  }

  /**
   * Crea una precompra. No toca el CartService: es un flujo paralelo.
   *
   * El comprobante del abono (30%) es OBLIGATORIO para sinpe/transfer y viaja
   * en la MISMA llamada como multipart (campo `image`): la precompra y el
   * comprobante se crean atómicamente — sin imagen no hay precompra
   * (create-on-finalize, mismo patrón que `CartService.cartToPreorder`).
   * `amount_paid` (30%) lo fija el backend server-side. Desde el Bloque 2 el
   * payload EXIGE `session_token`: la creación consume la reserva flotante.
   *
   * Mapea los 422/409 del backend a `PrepurchaseError` con un reason enum
   * usable por el modal para elegir el wording.
   */
  createWebPrepurchase(payload: PrepurchaseRequest, proof: File): Observable<Prepurchase> {
    return this.api
      .post<PrepurchaseEnvelope>('/new-web-prepurchase', this.toMultipart(payload, proof))
      .pipe(
        map(unwrapPrepurchase),
        catchError((err) => this.handlePrepurchaseError(err)),
      );
  }

  /**
   * Serializa el payload + comprobante a `FormData`. Omite `null`/`undefined`
   * (el backend los trata como ausentes, no como el string "null") y manda los
   * booleanos como `'1'`/`'0'`: la regla `boolean` de Laravel rechaza
   * `"true"`/`"false"` en multipart. Espejo de `CartService.toMultipart`.
   */
  private toMultipart(payload: PrepurchaseRequest, proof: File): FormData {
    const fd = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      fd.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
    });
    fd.append('image', proof);
    return fd;
  }

  /**
   * Mapea los mensajes literales del backend a razones enum. Los literales
   * viven en `PrepurchaseController.php` y `PrepurchaseSessionController.php`
   * y están en castellano. Comparamos en lowercase para tolerar mayúsculas y
   * acentos.
   */
  private handlePrepurchaseError(err: unknown): Observable<never> {
    if (err instanceof HttpErrorResponse) {
      const raw =
        flattenLaravelError(err.error?.error) ||
        flattenLaravelError(err.error?.errors) ||
        (typeof err.error?.message === 'string' ? err.error.message : '');
      const lower = raw.toLowerCase();

      // CUALQUIER 409 de la reserva/creación = cupo derivado agotado. El
      // mensaje es SIEMPRE el rótulo verbatim del copy centralizado (D14),
      // independientemente de lo que mande el backend.
      if (err.status === 409) {
        return throwError(
          () => new PrepurchaseError('sold_out', PREPURCHASE_COPY.reservation.soldOut),
        );
      }

      // 429 = rate-limiting de la apertura de reserva (hardening Bloque 2).
      // Rótulo verbatim; el modal lo trata como el 409 (no rompe el modal).
      if (err.status === 429) {
        return throwError(
          () => new PrepurchaseError('too_many_requests', PREPURCHASE_COPY.reservation.throttled),
        );
      }

      if (err.status === 422) {
        // Sesión inexistente o incoherente con producto/cantidad (Bloque 2):
        // el modal reabre una reserva nueva.
        if (lower.includes('tu reserva no es válida') || lower.includes('tu reserva no es valida')) {
          return throwError(
            () => new PrepurchaseError('invalid_session', PREPURCHASE_COPY.reservation.invalidSession),
          );
        }
        if (lower.includes('máximo de unidades') || lower.includes('sólo se puede precomprar')) {
          return throwError(() => new PrepurchaseError('limit_exceeded', raw));
        }
        if (lower.includes('sólo quedan') || lower.includes('solo quedan')) {
          return throwError(() => new PrepurchaseError('stock_exhausted', raw));
        }
        if (lower.includes('superado el límite de precompras') || lower.includes('superado el limite')) {
          return throwError(() => new PrepurchaseError('duplicate_monthly', raw));
        }
        return throwError(
          () => new PrepurchaseError('validation', raw || 'Datos no válidos.'),
        );
      }

      // Gate de Early Access (Perrón Points): defensa en profundidad, el check
      // primario ya vive en el cliente (prepurchase-modal). `err` siempre trae
      // {error, code, threshold, balance?} en este 403 — fallback genérico si
      // el código no casa (contrato inesperado).
      if (err.status === 403) {
        return throwError(
          () =>
            mapEarlyAccessGateError(err) ??
            new PrepurchaseError('unknown', 'No pudimos procesar tu reserva. Inténtalo de nuevo.'),
        );
      }

      if (err.status >= 500 || err.status === 0) {
        return throwError(
          () => new PrepurchaseError('network', 'Error de conexión. Inténtalo de nuevo.'),
        );
      }
    }

    return throwError(
      () => new PrepurchaseError('unknown', 'No pudimos procesar tu reserva. Inténtalo de nuevo.'),
    );
  }
}
